import { useEffect, useState } from "react";
import type { Platform } from "@helm/core";

type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !("MSStream" in window);
}

/**
 * Install affordances (SPEC §1A.2, §17.7):
 * - Chromium: capture `beforeinstallprompt` and show an "Install HELM" button.
 * - iOS: no prompt exists; show a one-time Share → Add to Home Screen sheet.
 * Hidden once running in standalone mode.
 */
export function InstallPrompt({ platform }: { platform: Platform }): React.JSX.Element | null {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (platform.isInstalled) return;
    const onBip = (e: Event): void => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    if (isIos()) {
      void platform.secureStore.get("ios-install-sheet-seen").then((seen) => {
        if (seen === null) setShowIos(true);
      });
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
    };
  }, [platform]);

  if (platform.isInstalled) return null;

  if (deferred) {
    return (
      <div className="install-card">
        <span>Install TradePrizm as an app for its own window, notifications and an app icon.</span>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            void deferred.prompt().then(() => {
              setDeferred(null);
            });
          }}
        >
          Install TradePrizm
        </button>
      </div>
    );
  }

  if (showIos) {
    return (
      <div className="install-card">
        <span>
          On iPhone: tap <strong>Share</strong> then <strong>Add to Home Screen</strong>. Push notifications
          only work from the installed app.
        </span>
        <button
          type="button"
          className="btn"
          onClick={() => {
            void platform.secureStore.set("ios-install-sheet-seen", "1");
            setShowIos(false);
          }}
        >
          Got it
        </button>
      </div>
    );
  }

  return null;
}
